import {
  DocumentMetadataType,
  TenantDocumentMetadata,
  MetadataEnum,
  WorkspaceDocumentMetadata,
} from "@/models/metadata/metadata";
import { isValid, parse } from "date-fns";

// Helper function to check if metadata has any valid values
export const hasValidMetadata = (
  metadata: DocumentMetadataType | undefined
) => {
  if (!metadata) return false;

  return (
    (metadata.stringValue !== undefined &&
      metadata.stringValue !== null &&
      metadata.stringValue !== "") ||
    (metadata.numberValue !== undefined && metadata.numberValue !== null) ||
    (metadata.booleanValue !== undefined && metadata.booleanValue !== null) ||
    (metadata.dateTimeValues !== undefined &&
      metadata.dateTimeValues !== null &&
      metadata.dateTimeValues.length > 0) ||
    (metadata.selectValues !== undefined &&
      metadata.selectValues !== null &&
      metadata.selectValues.length > 0)
  );
};

// Helper function to clean metadata by removing null/undefined values from arrays
// and filtering out invalid select values based on tenant configuration
export const cleanMetadata = (
  metadata: DocumentMetadataType | undefined,
  fieldName?: string,
  tenantFields?: TenantDocumentMetadata[]
) => {
  if (!metadata) return metadata;

  const cleaned: DocumentMetadataType = {};

  // Handle stringValue
  if (
    metadata.stringValue !== undefined &&
    metadata.stringValue !== null &&
    metadata.stringValue !== ""
  ) {
    cleaned.stringValue = metadata.stringValue;
  }

  // Handle numberValue
  if (metadata.numberValue !== undefined && metadata.numberValue !== null) {
    cleaned.numberValue = metadata.numberValue;
  }

  // Handle booleanValue
  if (metadata.booleanValue !== undefined && metadata.booleanValue !== null) {
    cleaned.booleanValue = metadata.booleanValue;
  }

  // Handle dateTimeValues - filter out null values from array and format string if it can be parsed to a date
  // Serialize dates as date-only strings to avoid timezone issues when sending to server
  if (
    metadata.dateTimeValues !== undefined &&
    metadata.dateTimeValues !== null
  ) {
    try {
      if (typeof metadata.dateTimeValues === "string") {
        const parsedDates = parseDate(metadata.dateTimeValues);
        if (parsedDates && parsedDates.length > 0) {
          cleaned.dateTimeValues = parsedDates;
        }
      } else if (Array.isArray(metadata.dateTimeValues)) {
        const normalizedDates = metadata.dateTimeValues
          .filter(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (date: any) => date !== null && date !== undefined
          )
          .map((date: Date | string | null | undefined) => {
            if (date instanceof Date) {
              // Normalize to UTC midnight to avoid timezone shifts when serializing
              return new Date(
                Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
              );
            }
            return date;
          })
          .filter((date): date is Date => date instanceof Date);
        if (normalizedDates.length > 0) {
          cleaned.dateTimeValues = normalizedDates;
        }
      } else {
        return undefined;
      }
    } catch {
      return undefined;
    }
  }

  // Handle selectValues - filter out null/undefined/empty values AND invalid values
  if (
    metadata.selectValues !== undefined &&
    metadata.selectValues !== null &&
    Array.isArray(metadata.selectValues)
  ) {
    let filteredSelects = metadata.selectValues.filter(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (value: any) => value !== null && value !== undefined && value !== ""
    );

    // If we have tenant fields and a field name, filter out invalid select values
    if (fieldName && tenantFields) {
      const tenantField = tenantFields.find(
        (f) =>
          f.name === fieldName &&
          f.metadataTypeEnum === MetadataEnum.SelectValues
      );
      if (tenantField?.selectValues) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        filteredSelects = filteredSelects.filter((value: any) =>
          tenantField.selectValues?.includes(value)
        );
      }
    }

    if (filteredSelects.length > 0) {
      cleaned.selectValues = filteredSelects;
    }
  }

  return cleaned;
};

/** Stable snapshot for comparing edited metadata against saved document metadata. */
export const serializeMetadataListForCompare = (
  list: WorkspaceDocumentMetadata[],
  tenantFields: TenantDocumentMetadata[]
): string => {
  const normalized = [...list]
    .map((item) => {
      const cleaned =
        cleanMetadata(item.metadata, item.name, tenantFields) ?? {};
      const metadataForCompare: DocumentMetadataType = { ...cleaned };
      if (
        metadataForCompare.selectValues &&
        metadataForCompare.selectValues.length > 0
      ) {
        metadataForCompare.selectValues = [
          ...metadataForCompare.selectValues,
        ].sort((a, b) => a.localeCompare(b));
      }
      return {
        id: item.id ?? null,
        name: item.name,
        externalPropertyName: item.externalPropertyName ?? null,
        metadata: metadataForCompare,
      };
    })
    .filter((item) => hasValidMetadata(item.metadata))
    .sort((a, b) => a.name.localeCompare(b.name));

  return JSON.stringify(normalized);
};

export const parseDate = (
  dateString: string | undefined
): Date[] | undefined => {
  if (!dateString) return undefined;
  const formattedDateString = dateString.split("T")[0];
  const possibleFormats = [
    "dd/MM/yyyy", // common European format
    "yyyy-MM-dd", // ISO-like format
    "dd-MM-yyyy", // common British format
  ].filter(Boolean) as string[];
  let parsedDate: Date[] | undefined = undefined;
  for (const fmt of possibleFormats) {
    const parsed = parse(formattedDateString, fmt, new Date());
    if (isValid(parsed)) {
      // Normalize to UTC midnight to avoid timezone shifts when serializing
      const utcMidnight = new Date(
        Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate())
      );
      parsedDate = [utcMidnight];
      break;
    }
  }
  return parsedDate;
};
